import { j as unwrapResult, k as codeSearch } from './code-bench-CmxRFbZe.mjs';
import { a as addEmptySpan, d as deleteSpan, b as detachCodeFromSpan, c as attachCodeToSpan, e as addCode, g as gradeUpdate, f as createCQATransaction } from './grade-bench-panel-BCC7rg7H.mjs';
import { useMemo, useCallback } from 'react';

const enrichingAnnotationHooks = {
    attachCode: async (dispatch, params)=>{
        return dispatch(attachCodeToSpan(params));
    },
    detachCode: async (dispatch, params)=>{
        return dispatch(detachCodeFromSpan(params));
    },
    deleteSpan: async (dispatch, params)=>{
        return dispatch(deleteSpan(params));
    },
    addEmptySpan: async (dispatch, spanData)=>{
        // RTK async thunks resolve to the action object; unwrapResult extracts the fulfilled payload (works with ThunkDispatch typing).
        const spanDocument = unwrapResult(await dispatch(addEmptySpan(spanData)));
        if (!spanDocument || !spanDocument.spans || spanDocument.spans.length === 0) {
            return null;
        }
        return spanDocument.spans[0];
    }
};

function _define_property$2(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$2(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$2(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$2(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$2(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$2(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
/**
 * Code search hook for grade-bench-panel and annotation components.
 * Can be used by:
 * - Annotation wrappers (with span context)
 * - Grade bench panel generalized (with transaction context)
 * 
 * Note: activeItem, userContext, processInstance, and searchResultsMap should be
 * obtained via useSelector in the calling component and passed in as parameters.
 */ const useCodeSearch = ({ dispatch, caseId, lookupKey, activeItem, userContext, processInstance, searchResultsMap = {
    v2: {},
    v1: {}
} })=>{
    // Compute search results for the current lookup key
    const searchResults = useMemo(()=>{
        const lookup = (lookupKey === null || lookupKey === void 0 ? void 0 : lookupKey.toString()) || '';
        if (!lookup) return [];
        // Try codeSearchResultsV2 first (structured by code type), fallback to searchResults
        const v2Results = searchResultsMap.v2[lookup];
        const v1Results = searchResultsMap.v1[lookup] || [];
        // If v2 exists, flatten it (it's structured by code type), otherwise return v1
        if (v2Results && typeof v2Results === 'object' && !Array.isArray(v2Results)) {
            return [
                ...v2Results['CPT'] || [],
                ...v2Results['ICD'] || [],
                ...v2Results['MODIFIER'] || []
            ];
        }
        return Array.isArray(v2Results) ? v2Results : v1Results;
    }, [
        searchResultsMap,
        lookupKey
    ]);
    /**
   * Dispatch code search action
   * @param codeQuery - The code query with text, searchType, etc.
   * @param context - Additional context (transaction, span, invoice, etc.)
   * @param searchResultsLookupKey - Optional override for lookup key
   */ const onCodeSearch = useCallback((codeQuery, context, searchResultsLookupKey)=>{
        const effectiveLookupKey = searchResultsLookupKey || lookupKey;
        // Build full context with required fields
        // activeItem and userContext are required for codeSearch action (derived from Redux state)
        const fullContext = _object_spread_props$2(_object_spread$2({}, codeQuery.context), {
            activeItem: (context === null || context === void 0 ? void 0 : context.activeItem) || activeItem,
            userContext: (context === null || context === void 0 ? void 0 : context.userContext) || userContext,
            transaction: context === null || context === void 0 ? void 0 : context.transaction,
            invoice: context === null || context === void 0 ? void 0 : context.invoice,
            processInstance: (context === null || context === void 0 ? void 0 : context.processInstance) || processInstance,
            selectedSpan: context === null || context === void 0 ? void 0 : context.selectedSpan
        });
        const codeQueryWithContext = _object_spread_props$2(_object_spread$2({}, codeQuery), {
            context: fullContext,
            searchResultsLookupKey: effectiveLookupKey === null || effectiveLookupKey === void 0 ? void 0 : effectiveLookupKey.toString()
        });
        dispatch(codeSearch(codeQueryWithContext));
    }, [
        dispatch,
        lookupKey,
        activeItem,
        userContext,
        processInstance
    ]);
    /**
   * Handle code selection from search results
   * Uses addCode from grade-bench-panel-actions which supports spans for annotations
   * 
   * @param codeAction - The selected code action payload
   * @param context - Additional context (transaction, span, invoice, etc.)
   */ const onCodeSearchSelection = useCallback((codeAction, context)=>{
        var _codeAction_context, _codeAction_context1, _codeAction_context2, _codeAction_context3;
        // Build the payload for grade-bench-panel addCode action
        // This supports spans for annotations
        const payload = {
            codeInfo: codeAction.codeInfo,
            context: _object_spread_props$2(_object_spread$2({}, codeAction.context), {
                transaction: (context === null || context === void 0 ? void 0 : context.transaction) || ((_codeAction_context = codeAction.context) === null || _codeAction_context === void 0 ? void 0 : _codeAction_context.transaction),
                invoice: (context === null || context === void 0 ? void 0 : context.invoice) || ((_codeAction_context1 = codeAction.context) === null || _codeAction_context1 === void 0 ? void 0 : _codeAction_context1.invoice),
                processInstance: (context === null || context === void 0 ? void 0 : context.processInstance) || processInstance || ((_codeAction_context2 = codeAction.context) === null || _codeAction_context2 === void 0 ? void 0 : _codeAction_context2.processInstance),
                activeItem: (context === null || context === void 0 ? void 0 : context.activeItem) || activeItem || ((_codeAction_context3 = codeAction.context) === null || _codeAction_context3 === void 0 ? void 0 : _codeAction_context3.activeItem)
            }),
            documentId: context === null || context === void 0 ? void 0 : context.documentId,
            caseId: (context === null || context === void 0 ? void 0 : context.caseId) || caseId,
            spans: context === null || context === void 0 ? void 0 : context.spans
        };
        // Use grade-bench-panel addCode which supports spans
        dispatch(addCode(payload));
    }, [
        dispatch,
        caseId,
        activeItem,
        processInstance
    ]);
    /**
   * Get search results for a specific lookup key
   * @param key - The lookup key (transaction ID, span ID, etc.)
   * @param codeType - Optional code type filter for v2 results
   */ const getSearchResults = useMemo(()=>{
        return (key, codeType)=>{
            const lookup = (key === null || key === void 0 ? void 0 : key.toString()) || (lookupKey === null || lookupKey === void 0 ? void 0 : lookupKey.toString()) || '';
            if (!lookup) return [];
            const v2Results = searchResultsMap.v2[lookup];
            const v1Results = searchResultsMap.v1[lookup] || [];
            console.log('in getSearchResults lookup', lookup, 'v2Results', v2Results, 'v1Results', v1Results);
            if (v2Results && typeof v2Results === 'object' && !Array.isArray(v2Results)) {
                if (codeType) {
                    return v2Results[codeType] || [];
                }
                // Flatten all code types
                console.log('in getting v2 flattend');
                return [
                    ...v2Results['CPT'] || [],
                    ...v2Results['MODIFIER'] || [],
                    ...v2Results['ICD'] || []
                ];
            }
            console.log('in getting v2 not flattend');
            console.log('v2Results', v2Results, 'v1Results', v1Results);
            return Array.isArray(v2Results) ? v2Results : v1Results;
        };
    }, [
        searchResultsMap,
        lookupKey
    ]);
    return {
        onCodeSearch,
        onCodeSearchSelection,
        getSearchResults,
        searchResults
    };
};

function _define_property$1(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread$1(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property$1(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys$1(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props$1(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys$1(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
/**
 * Hook for creating CQA transactions
 * Provides a reusable way to create transactions for invoices
 * 
 * @param dispatch - Redux dispatch function
 * @returns Object with createTransaction function
 */ const useCreateTransaction = (dispatch)=>{
    /**
   * Create a transaction for an invoice
   * @param invoiceId - The ID of the invoice to create a transaction for
   * @returns Promise that resolves to the created transaction or null if it fails
   */ const createTransaction = useCallback(async (invoiceId)=>{
        try {
            const result = await dispatch(createCQATransaction({
                invoiceId
            })).unwrap();
            // The dispatch returns the result from the async action
            return result;
        } catch (error) {
            console.error('Error creating transaction:', error);
            return null;
        }
    }, [
        dispatch
    ]);
    return {
        createTransaction
    };
};
/**
 * Hook for creating a CQA transaction and adding a code in one operation
 * Always creates a new transaction first, then adds the code to it
 * 
 * @param dispatch - Redux dispatch function
 * @returns Object with createTransactionAndAddCode function
 */ const useCreateTransactionAndAddCode = (dispatch)=>{
    /**
   * Create a transaction and add a code to it
   * @param payload - The code add action payload with invoice context
   * @returns Promise that resolves to the code add payload or null if it fails
   */ const createTransactionAndAddCode = useCallback(async (payload)=>{
        const { context } = payload;
        const invoice = context === null || context === void 0 ? void 0 : context.invoice;
        if (!(invoice === null || invoice === void 0 ? void 0 : invoice.id)) {
            console.error('Invoice ID is required to create transaction');
            return null;
        }
        // Always create a new transaction first
        const transaction = await dispatch(createCQATransaction({
            invoiceId: invoice.id
        })).unwrap();
        if (!transaction) {
            // Transaction creation failed - Redux store will handle error messages
            return null;
        }
        // Add the code with the newly created transaction
        const codePayload = _object_spread_props$1(_object_spread$1({}, payload), {
            context: _object_spread_props$1(_object_spread$1({}, context), {
                transaction
            })
        });
        return await dispatch(addCode(codePayload)).unwrap();
    }, [
        dispatch
    ]);
    return {
        createTransactionAndAddCode
    };
};
/**
 * Hook for updating code assessment or role
 * Provides a reusable way to update code assessments (assessment/role fields)
 * 
 * @param dispatch - Redux dispatch function
 * @returns Object with updateCodeAssessment function
 */ const useCodeAssessmentUpdate = (dispatch)=>{
    /**
   * Update a code's assessment or role
   * @param changeEvent - The grade bench select event with code and field information
   * @returns Promise that resolves to null (gradeUpdate returns null)
   */ const updateCodeAssessment = useCallback(async (changeEvent)=>{
        try {
            await dispatch(gradeUpdate(changeEvent)).unwrap();
        } catch (error) {
            console.error('Error updating code assessment:', error);
            throw error;
        }
    }, [
        dispatch
    ]);
    return {
        updateCodeAssessment
    };
};

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
/**
 * Extracts all spans from a CQA work item's invoices structure
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @returns Array of all spans found across all invoices, transactions, and code types
 */ const getAllSpans = (invoices)=>{
    const allSpans = [];
    invoices.forEach((invoice)=>{
        invoice.transactions.forEach((transaction)=>{
            // Extract spans from procedures
            transaction.procedures.forEach((procedure)=>{
                if (procedure.spans) {
                    allSpans.push(...procedure.spans);
                }
            });
            // Extract spans from diagnoses
            transaction.diagnoses.forEach((diagnosis)=>{
                if (diagnosis.spans) {
                    allSpans.push(...diagnosis.spans);
                }
            });
            // Extract spans from modifiers
            transaction.modifiers.forEach((modifier)=>{
                if (modifier.spans) {
                    allSpans.push(...modifier.spans);
                }
            });
        });
    });
    return allSpans;
};
/**
 * Merge attributes for spans that overlap with identical begin/end offsets.
 * If two or more spans share the same [beginOffset, endOffset], their attributes are concatenated
 * into a single span instance. Order of attributes is preserved by invoice traversal order.
 */ const mergeOverlappingSpanAttributes = (spans)=>{
    if (!spans || spans.length === 0) return [];
    const map = new Map();
    for (const span of spans){
        const key = `${span.beginOffset}:${span.endOffset}`;
        const existing = map.get(key);
        if (existing) {
            const existingAttrs = Array.isArray(existing.attributes) ? existing.attributes : [];
            const newAttrs = Array.isArray(span.attributes) ? span.attributes : [];
            const existingRubricAttrs = Array.isArray(existing.rubricAttributes) ? existing.rubricAttributes : [];
            const newRubricAttrs = Array.isArray(span.rubricAttributes) ? span.rubricAttributes : [];
            // Create a merged span preserving first seen id/coveredText; append attributes
            map.set(key, _object_spread(_object_spread_props(_object_spread({}, existing), {
                attributes: [
                    ...existingAttrs,
                    ...newAttrs
                ]
            }), existingRubricAttrs.length || newRubricAttrs.length ? {
                rubricAttributes: [
                    ...existingRubricAttrs,
                    ...newRubricAttrs
                ]
            } : {}));
        } else {
            map.set(key, _object_spread_props(_object_spread({}, span), {
                attributes: Array.isArray(span.attributes) ? span.attributes : []
            }));
        }
    }
    // Return in stable order based on beginOffset, then endOffset
    return Array.from(map.values()).sort((a, b)=>{
        if (a.beginOffset !== b.beginOffset) {
            return a.beginOffset - b.beginOffset;
        }
        return a.endOffset - b.endOffset;
    });
};
/**
 * Gets all spans with their associated code information
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @returns Array of objects containing span and associated code metadata
 */ const getAllSpansWithCodeInfo = (invoices)=>{
    const spansWithInfo = [];
    invoices.forEach((invoice)=>{
        invoice.transactions.forEach((transaction)=>{
            // Process procedures
            transaction.procedures.forEach((procedure)=>{
                if (procedure.spans) {
                    procedure.spans.forEach((span)=>{
                        spansWithInfo.push({
                            span,
                            codeType: 'procedure',
                            code: procedure,
                            transaction,
                            invoice
                        });
                    });
                }
            });
            // Process diagnoses
            transaction.diagnoses.forEach((diagnosis)=>{
                if (diagnosis.spans) {
                    diagnosis.spans.forEach((span)=>{
                        spansWithInfo.push({
                            span,
                            codeType: 'diagnosis',
                            code: diagnosis,
                            transaction,
                            invoice
                        });
                    });
                }
            });
            // Process modifiers
            transaction.modifiers.forEach((modifier)=>{
                if (modifier.spans) {
                    modifier.spans.forEach((span)=>{
                        spansWithInfo.push({
                            span,
                            codeType: 'modifier',
                            code: modifier,
                            transaction,
                            invoice
                        });
                    });
                }
            });
        });
    });
    return spansWithInfo;
};
/**
 * Finds a span by ID across all invoices, transactions, and codes
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param spanId - The ID of the span to find
 * @returns The span if found, or null if not found
 */ const findSpanById = (invoices, spanId)=>{
    const spansWithInfo = getAllSpansWithCodeInfo(invoices);
    const spanInfo = spansWithInfo.find((info)=>info.span.id === spanId);
    return spanInfo ? spanInfo.span : null;
};
/**
 * Finds all codes that have a specific span attached
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param spanId - The ID of the span to find codes for
 * @returns Array of objects containing code, codeType, transaction, and invoice for each code that has the span
 */ const findCodesBySpanId = (invoices, spanId)=>{
    const spansWithInfo = getAllSpansWithCodeInfo(invoices);
    return spansWithInfo.filter((info)=>info.span.id === spanId).map((info)=>({
            code: info.code,
            codeType: info.codeType,
            transaction: info.transaction,
            invoice: info.invoice
        }));
};
/**
 * Extract ALL codes from invoices
 * Each instance of a code is kept separate
 * @param invoices - Array of CQA invoices containing transactions and codes
 * @param codeDescriptions - Optional map of code descriptions
 * @returns Array of extracted codes with metadata
 */ const extractCodesFromInvoices = (invoices, codeDescriptions = {})=>{
    const codes = [];
    if (!invoices || !Array.isArray(invoices)) return codes;
    invoices.forEach((invoice)=>{
        var _invoice_transactions;
        const invoiceId = invoice.id;
        (_invoice_transactions = invoice.transactions) === null || _invoice_transactions === void 0 ? void 0 : _invoice_transactions.forEach((transaction)=>{
            var // Extract procedures - each instance is separate
            _transaction_procedures, // Extract modifiers - each instance is separate
            _transaction_modifiers, // Extract diagnoses - each instance is separate
            _transaction_diagnoses;
            const transactionId = transaction.id;
            (_transaction_procedures = transaction.procedures) === null || _transaction_procedures === void 0 ? void 0 : _transaction_procedures.forEach((proc, procIdx)=>{
                if (proc.code) {
                    var _proc_spans_, _proc_spans;
                    const codeInfo = codeDescriptions[proc.code];
                    codes.push(_object_spread_props(_object_spread({}, proc), {
                        codeId: proc.id,
                        codeType: 'procedure',
                        value: proc.code,
                        compositeId: `inv-${invoiceId}-tx-${transactionId}-proc-${proc.id}-${procIdx}`,
                        description: (codeInfo === null || codeInfo === void 0 ? void 0 : codeInfo.description) || proc.description || '',
                        invoiceId,
                        transactionId,
                        spanId: ((_proc_spans = proc.spans) === null || _proc_spans === void 0 ? void 0 : (_proc_spans_ = _proc_spans[0]) === null || _proc_spans_ === void 0 ? void 0 : _proc_spans_.id) || 0,
                        spans: proc.spans || []
                    }));
                }
            });
            (_transaction_modifiers = transaction.modifiers) === null || _transaction_modifiers === void 0 ? void 0 : _transaction_modifiers.forEach((mod, modIdx)=>{
                if (mod.code) {
                    var _mod_spans_, _mod_spans;
                    const codeInfo = codeDescriptions[mod.code];
                    codes.push(_object_spread_props(_object_spread({}, mod), {
                        codeId: mod.id,
                        codeType: 'modifier',
                        value: mod.code,
                        compositeId: `inv-${invoiceId}-tx-${transactionId}-mod-${mod.id}-${modIdx}`,
                        description: (codeInfo === null || codeInfo === void 0 ? void 0 : codeInfo.description) || mod.description || '',
                        invoiceId,
                        transactionId,
                        spanId: ((_mod_spans = mod.spans) === null || _mod_spans === void 0 ? void 0 : (_mod_spans_ = _mod_spans[0]) === null || _mod_spans_ === void 0 ? void 0 : _mod_spans_.id) || 0,
                        spans: mod.spans || []
                    }));
                }
            });
            (_transaction_diagnoses = transaction.diagnoses) === null || _transaction_diagnoses === void 0 ? void 0 : _transaction_diagnoses.forEach((diag, diagIdx)=>{
                if (diag.code) {
                    var _diag_spans_, _diag_spans;
                    const codeInfo = codeDescriptions[diag.code];
                    codes.push(_object_spread_props(_object_spread({}, diag), {
                        codeId: diag.id,
                        codeType: 'diagnosis',
                        value: diag.code,
                        compositeId: `inv-${invoiceId}-tx-${transactionId}-diag-${diag.id}-${diagIdx}`,
                        description: (codeInfo === null || codeInfo === void 0 ? void 0 : codeInfo.description) || diag.description || '',
                        invoiceId,
                        transactionId,
                        spanId: ((_diag_spans = diag.spans) === null || _diag_spans === void 0 ? void 0 : (_diag_spans_ = _diag_spans[0]) === null || _diag_spans_ === void 0 ? void 0 : _diag_spans_.id) || 0,
                        spans: diag.spans || []
                    }));
                }
            });
        });
    });
    return codes;
};
/**
 * Calculate available codes by removing used codes from invoice codes
 * Removes one instance of each used code (preserves duplicates)
 * @param allInvoiceCodes - All codes extracted from invoices
 * @param usedCodes - Codes that are already assigned to spans
 * @returns Array of available codes that can still be assigned
 * @deprecated Use direct filtering in components instead. This function removes codes attached to ANY span,
 * but codes should remain available for other spans. Use: allInvoiceCodes.filter(code => !code.spans?.some(span => span.id === selectedSpanId))
 */ const calculateAvailableCodes = (allInvoiceCodes, usedCodes)=>{
    // Create a copy to avoid mutating original
    const availableCodes = [
        ...allInvoiceCodes
    ];
    // Create a map to track how many times each code is used
    const usedCodeCounts = new Map();
    usedCodes.forEach((used)=>{
        const key = `${used.codeType}-${used.value}`;
        usedCodeCounts.set(key, (usedCodeCounts.get(key) || 0) + 1);
    });
    // Remove used codes (one instance per usage)
    const filteredCodes = [];
    const removedCounts = new Map();
    availableCodes.forEach((code)=>{
        const key = `${code.codeType}-${code.value}`;
        const usedCount = usedCodeCounts.get(key) || 0;
        const alreadyRemoved = removedCounts.get(key) || 0;
        if (alreadyRemoved < usedCount) {
            // This instance should be removed (already used)
            removedCounts.set(key, alreadyRemoved + 1);
        } else {
            // This instance is available
            filteredCodes.push(code);
        }
    });
    return filteredCodes;
};
/**
 * Calculate text selection offsets from DOM selection
 * Handles offset calculation, whitespace trimming, and word boundary snapping
 * @param container - The container element containing the text
 * @param selection - The window Selection object
 * @param range - The Range object from the selection
 * @param text - The full text content of the document
 * @returns Object with startIndex, endIndex, and selectedText, or null if invalid
 */ const calculateTextSelectionOffsets = (container, selection, range, text)=>{
    if (!selection || selection.toString().length === 0) {
        return null;
    }
    // Calculate absolute positions in the document
    const calculateAbsoluteOffset = (node, offset)=>{
        let absoluteOffset = 0;
        let currentPos = 0;
        // Walk through all text nodes to find the target node and calculate position
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
        let textNode;
        while(textNode = walker.nextNode()){
            const textContent = textNode.textContent || '';
            if (textNode === node) {
                // Found our node, add the offset within this node
                absoluteOffset = currentPos + offset;
                break;
            }
            // Add the full length of this text node
            currentPos += textContent.length;
        }
        return absoluteOffset;
    };
    // Calculate absolute start and end positions
    let startIndex = calculateAbsoluteOffset(range.startContainer, range.startOffset);
    let endIndex = calculateAbsoluteOffset(range.endContainer, range.endOffset);
    // trim leading whitespace
    while(startIndex < endIndex && /\s/.test(text[startIndex])){
        startIndex++;
    }
    // trim trailing whitespace
    while(endIndex > startIndex && /\s/.test(text[endIndex - 1])){
        endIndex--;
    }
    // Now snap to word boundaries only if we're in the middle of a word
    // Expand backwards to start of word only if current position is mid-word
    if (startIndex > 0 && /\S/.test(text[startIndex]) && /\S/.test(text[startIndex - 1])) {
        while(startIndex > 0 && /\S/.test(text[startIndex - 1])){
            startIndex--;
        }
    }
    // Expand forwards to end of word only if current position is mid-word
    if (endIndex < text.length && /\S/.test(text[endIndex - 1]) && /\S/.test(text[endIndex])) {
        while(endIndex < text.length && /\S/.test(text[endIndex])){
            endIndex++;
        }
    }
    // Get the final selected text after snapping and trimming
    const finalSelectedText = text.slice(startIndex, endIndex);
    // Only proceed if we have a valid selection after trimming
    if (finalSelectedText.length === 0) {
        return null;
    }
    return {
        startIndex,
        endIndex,
        selectedText: finalSelectedText
    };
};
/**
 * Maps an ExtractedCode to SuggestedCode format
 * @param code - The extracted code to convert
 * @returns A SuggestedCode object compatible with code suggestion UI components
 */ const mapExtractedCodetoSuggestedCode = (code)=>{
    var _code_spans;
    return {
        code: code.value,
        codeType: code.codeType === 'procedure' ? 'CPT' : code.codeType === 'diagnosis' ? 'ICD' : 'MODIFIER',
        description: code.description || '',
        code_source: 'CURRENT_CASE',
        code_recent_date: new Date().toISOString(),
        suggested_code_type: 'CURRENT_CASE',
        transaction_guids: code === null || code === void 0 ? void 0 : (_code_spans = code.spans) === null || _code_spans === void 0 ? void 0 : _code_spans.map((span)=>{
            var _span_id;
            return span === null || span === void 0 ? void 0 : (_span_id = span.id) === null || _span_id === void 0 ? void 0 : _span_id.toString();
        }),
        effectiveDate: '',
        shortDescription: code.description || '',
        tenantDefined: false,
        codeId: code.codeId
    };
};

export { calculateTextSelectionOffsets as a, extractCodesFromInvoices as b, calculateAvailableCodes as c, useCodeSearch as d, enrichingAnnotationHooks as e, useCreateTransaction as f, useCreateTransactionAndAddCode as g, getAllSpans as h, mergeOverlappingSpanAttributes as i, findCodesBySpanId as j, findSpanById as k, mapExtractedCodetoSuggestedCode as m, useCodeAssessmentUpdate as u };
//# sourceMappingURL=span-utils-BkM9Ieri.mjs.map
